import { supabase } from '../supabase.js';
import { KINDS, KIND_LABELS, describe, kindOf, newestFirst, whoName } from '../lib/admin-words.js';

/* ============================================================
   The audit log (docs/admin-area.md): who changed what, and
   when, in plain words. One read (list_audit_entries) of the
   newest rows, already named and trimmed by the database;
   filtering happens here in the browser. Direct-SQL and cron
   changes have no actor; they are off by default and one
   click away.
============================================================ */

const TZ = 'Africa/Johannesburg';
const dayFmt  = new Intl.DateTimeFormat('en-ZA', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const timeFmt = new Intl.DateTimeFormat('en-ZA', { timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: false });
const keyFmt  = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' });

let _state = { kind: 'all', who: '', q: '', system: false };
let _entries = [];

export async function renderAdminAudit() {
  const el = document.getElementById('view-admin-audit');
  if (!window.__access?.admin) {
    el.innerHTML = `<div class="page-header"><h1>Audit log</h1><p>The admin area is not switched on for you.</p></div>`;
    return;
  }

  el.innerHTML = `
    <div class="page-header">
      <h1>Audit log</h1>
      <p>Who changed what, and when: learners, enrolments, classes, staff, payslips and access. Newest first, times in SAST.</p>
    </div>
    <div class="filter-row aa-filters" id="aa-kinds"></div>
    <div class="filter-row aa-filters">
      <select id="aa-who" class="select-input" aria-label="Filter by person"><option value="">Everyone</option></select>
      <input type="search" id="aa-q" class="select-input" placeholder="Search…" aria-label="Search the log" value="${esc(_state.q)}" />
      <label class="aa-system"><input type="checkbox" id="aa-system" ${_state.system ? 'checked' : ''} /> Include changes made by the system</label>
      <span class="ap-count" id="aa-count"></span>
    </div>
    <div id="aa-list"><div class="no-data">Loading…</div></div>
  `;

  el.querySelector('#aa-q').addEventListener('input', (e) => { _state.q = e.target.value; draw(); });
  el.querySelector('#aa-who').addEventListener('change', (e) => { _state.who = e.target.value; draw(); });
  el.querySelector('#aa-system').addEventListener('change', async (e) => { _state.system = e.target.checked; await load(); });

  await load();
}

async function load() {
  const list = document.getElementById('aa-list');
  if (!list) return;
  list.innerHTML = `<div class="no-data">Loading…</div>`;
  const { data, error } = await supabase.rpc('list_audit_entries', { p_limit: 400, p_people_only: !_state.system });
  if (error) { list.innerHTML = `<div class="no-data">Could not read the log: ${esc(error.message)}</div>`; return; }
  _entries = (data ?? []).map((e) => ({ ...e, kind: kindOf(e.table_name), who: whoName(e), what: describe(e) })).sort(newestFirst);

  const whoSel = document.getElementById('aa-who');
  const people = [...new Set(_entries.map((e) => e.who))].sort();
  whoSel.innerHTML = `<option value="">Everyone</option>${people.map((p) => `<option value="${esc(p)}" ${p === _state.who ? 'selected' : ''}>${esc(p)}</option>`).join('')}`;
  draw();
}

function draw() {
  const list = document.getElementById('aa-list');
  const kindsEl = document.getElementById('aa-kinds');
  const count = document.getElementById('aa-count');
  if (!list) return;

  const counts = { all: _entries.length };
  for (const k of KINDS) counts[k] = 0;
  for (const e of _entries) counts[e.kind] += 1;
  kindsEl.innerHTML = ['all', ...KINDS].map((k) => `
    <button type="button" class="filter-btn ${k === _state.kind ? 'active' : ''}" data-kind="${k}" aria-pressed="${k === _state.kind}">
      ${k === 'all' ? 'Everything' : KIND_LABELS[k]} (${counts[k]})
    </button>`).join('');
  kindsEl.querySelectorAll('.filter-btn').forEach((b) => b.addEventListener('click', () => { _state.kind = b.dataset.kind; draw(); }));

  const needle = _state.q.trim().toLowerCase();
  const shown = _entries.filter((e) =>
    (_state.kind === 'all' || e.kind === _state.kind) &&
    (!_state.who || e.who === _state.who) &&
    (!needle || `${e.who} ${e.what}`.toLowerCase().includes(needle)));
  if (count) count.textContent = `${shown.length} shown`;

  if (!shown.length) {
    list.innerHTML = `<div class="no-data">${_entries.length ? 'Nothing matches. Clear the search or pick another filter.' : 'Nothing recorded by a person yet. Tick “Include changes made by the system” to see data loads and automatic updates.'}</div>`;
    return;
  }

  const days = [];
  for (const e of shown) {
    const d = new Date(e.at);
    const key = keyFmt.format(d);
    const last = days[days.length - 1];
    if (last && last.key === key) last.items.push(e);
    else days.push({ key, label: dayFmt.format(d), items: [e] });
  }

  list.innerHTML = days.map((d) => `
    <div class="table-card aa-day">
      <div class="table-card-header"><div class="table-card-title">${d.label}</div><div class="table-card-sub">${d.items.length} change${d.items.length === 1 ? '' : 's'}</div></div>
      <ul class="aa-items">
        ${d.items.map((e) => `
          <li class="aa-item">
            <span class="aa-time">${timeFmt.format(new Date(e.at))}</span>
            <span class="aa-what"><strong>${esc(e.who)}</strong> ${esc(e.what)}</span>
            <span class="aa-kind">${KIND_LABELS[e.kind]}</span>
          </li>`).join('')}
      </ul>
    </div>`).join('');
}

function esc(s) { return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
